import { useEffect, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { problemMessage } from '../../api/baseApi'
import {
  useGetOperatorTenantQuery,
  useOffboardTenantMutation,
  useReinstateTenantMutation,
  useRetryPurgeMutation,
  useUpdateTenantMutation,
  type OperatedTenant,
} from '../../api/operator'
import { reinstatement } from '../../model/reinstatement'
import { operator, routes } from '../../model/routes'
import { Loading, QueryError } from '../../ui/QueryState'
import { OperatorHeader, StatusChip } from './common'
import { TenantAdministrators } from './TenantAdministrators'
import { TenantBilling } from './TenantBilling'
import { TenantDomains } from './TenantDomains'
import { TenantTraffic } from './TenantTraffic'
import { shown } from '../../api/useIntent'

export type TenantFace = 'overview' | 'billing' | 'administrators' | 'domains' | 'traffic'

/**
 * One customer. Five faces, each a path, for the reason every screen in
 * this client puts its faces in the path: a link to a tenant's statement
 * should open the statement.
 */
export function OperatorTenant({ face = 'overview' }: { face?: TenantFace }) {
  const { tenantId = '' } = useParams()
  const tenant = useGetOperatorTenantQuery({ tenant: tenantId })

  // While a purge runs, its progress is what the operator is waiting to
  // see, so the tenant is re-read until it settles.
  const purging = tenant.data?.status === 'offboarding'
  useEffect(() => {
    if (!purging) return
    const timer = window.setInterval(() => void tenant.refetch(), 3000)
    return () => window.clearInterval(timer)
  }, [purging, tenant])

  if (tenant.isLoading) return <Loading label="Loading tenant…" />
  if (tenant.error || !tenant.data) return <QueryError error={tenant.error} title="Could not load the tenant" />
  const data = tenant.data

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Link component={RouterLink} to={operator.tenants()} underline="hover" variant="body2">
          ← Tenants
        </Link>
        <OperatorHeader
          title={
            <Stack direction="row" spacing={1.5} alignItems="center" component="span">
              <span>{data.name}</span>
              <StatusChip status={data.status} />
            </Stack>
          }
          subtitle={data.id}
          actions={
            data.status === 'active' && (
              <Button component={RouterLink} to={routes.tenant(data.id)}>
                Open content
              </Button>
            )
          }
        />
        <Tabs value={face} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Overview" value="overview" component={RouterLink} to={operator.tenant(data.id)} />
          <Tab label="Billing" value="billing" component={RouterLink} to={operator.tenantBilling(data.id)} />
          <Tab label="Administrators" value="administrators" component={RouterLink} to={operator.tenantAdministrators(data.id)} />
          <Tab label="Domains" value="domains" component={RouterLink} to={operator.tenantDomains(data.id)} />
          <Tab label="Traffic" value="traffic" component={RouterLink} to={operator.tenantTraffic(data.id)} />
        </Tabs>
      </Stack>

      {face === 'overview' && <Overview tenant={data} />}
      {face === 'billing' && <TenantBilling tenant={data.id} active={data.status === 'active'} />}
      {face === 'administrators' && <TenantAdministrators tenant={data.id} active={data.status === 'active'} />}
      {face === 'domains' && <TenantDomains tenant={data.id} active={data.status === 'active'} />}
      {face === 'traffic' && <TenantTraffic tenant={data.id} active={data.status === 'active'} />}
    </Stack>
  )
}

function Overview({ tenant }: { tenant: OperatedTenant }) {
  return (
    <Stack spacing={3}>
      {tenant.status !== 'active' && <PurgeProgress tenant={tenant} />}
      <Details tenant={tenant} />
      <Paper sx={{ p: 2 }}>
        <Typography variant="h3" sx={{ mb: 1 }}>
          Log
        </Typography>
        <List dense disablePadding>
          {tenant.log.map((entry, index) => (
            <ListItem key={`${entry.at}-${index}`} disableGutters>
              <ListItemText
                primary={
                  <>
                    <strong>{entry.action}</strong>
                    {entry.detail && <> — {entry.detail}</>}
                  </>
                }
                secondary={`${new Date(entry.at).toLocaleString()} · ${entry.actor.email ?? entry.actor.subject}`}
              />
            </ListItem>
          ))}
        </List>
      </Paper>
      {tenant.status === 'active' && <Offboard tenant={tenant} />}
      <Reinstate tenant={tenant} />
    </Stack>
  )
}

function Details({ tenant }: { tenant: OperatedTenant }) {
  const [update, saving] = useUpdateTenantMutation()
  const [name, setName] = useState(tenant.name)
  const [contactName, setContactName] = useState(tenant.contactName ?? '')
  const [contactEmail, setContactEmail] = useState(tenant.contactEmail ?? '')
  const [notes, setNotes] = useState(tenant.notes ?? '')
  const changed =
    name !== tenant.name ||
    contactName !== (tenant.contactName ?? '') ||
    contactEmail !== (tenant.contactEmail ?? '') ||
    notes !== (tenant.notes ?? '')

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h3">Details</Typography>
        {Boolean(saving.error) && <Alert severity="error">{problemMessage(saving.error, 'The details were not saved.')}</Alert>}
        <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField label="Billing contact" value={contactName} onChange={(event) => setContactName(event.target.value)} fullWidth />
          <TextField label="Billing email" value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} fullWidth />
        </Stack>
        <TextField label="Notes" value={notes} onChange={(event) => setNotes(event.target.value)} multiline minRows={2} />
        <Typography variant="caption" color="text.secondary">
          Registered {new Date(tenant.createdAt).toLocaleDateString()} by {tenant.createdBy.email ?? tenant.createdBy.subject}
        </Typography>
        <Box>
          <Button
            variant="contained"
            disabled={!changed || !name.trim() || saving.isLoading}
            onClick={() =>
              update({ tenant: tenant.id, tenantDetails: { name: name.trim(), contactName, contactEmail, notes } }).catch(shown)
            }
          >
            Save
          </Button>
        </Box>
      </Stack>
    </Paper>
  )
}

function PurgeProgress({ tenant }: { tenant: OperatedTenant }) {
  const [retry, retrying] = useRetryPurgeMutation()
  const report = tenant.purge?.report
  const removed = report
    ? `${report.eventRows} event rows, ${report.readRows} read model rows, ${report.domainRows} domain rows (grants and bindings), ` +
      `${report.memberships} administrators, ${report.webhookRows} webhook rows, ${report.trafficRows} traffic rows, ` +
      `${report.publicationOperations} publication operations, ${report.payloadObjects} payload objects and ${report.blobs} uploads`
    : undefined

  if (tenant.status === 'offboarded') {
    return (
      <Alert severity="info">
        <AlertTitle>Offboarded {tenant.offboardedAt && new Date(tenant.offboardedAt).toLocaleDateString()}</AlertTitle>
        Its content is deleted{removed && <>: {removed}</>}. The record and its billing history remain, and its id will
        not be given to another tenant.
      </Alert>
    )
  }
  return (
    <Alert
      severity={tenant.purge?.error ? 'error' : 'warning'}
      action={
        tenant.purge?.error && (
          <Button color="inherit" size="small" disabled={retrying.isLoading} onClick={() => retry({ tenant: tenant.id }).catch(shown)}>
            Retry
          </Button>
        )
      }
    >
      <AlertTitle>{tenant.purge?.error ? 'Deleting this tenant’s content stopped' : 'Deleting this tenant’s content'}</AlertTitle>
      {tenant.purge?.error ? (
        <>
          Attempt {tenant.purge.attempts} failed: {tenant.purge.error}. Running it again is safe; it continues from where it
          stopped.
        </>
      ) : (
        <>Access has been withdrawn and billing has ended. {removed ? <>Removed so far: {removed}.</> : 'Starting…'}</>
      )}
      {Boolean(retrying.error) && <Box sx={{ mt: 1 }}>{problemMessage(retrying.error)}</Box>}
    </Alert>
  )
}

/**
 * The one destructive action, and one confirmation in front of it.
 *
 * The dialog names the tenant and says what stops working, because a
 * static site keeps serving after its content is deleted: its forms and
 * its next build are what break, and neither is obvious from here.
 */
function Offboard({ tenant }: { tenant: OperatedTenant }) {
  const [open, setOpen] = useState(false)
  const [offboard, offboarding] = useOffboardTenantMutation()

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: 'error.light' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }}>
        <Box>
          <Typography variant="h3">Offboard</Typography>
          <Typography variant="body2" color="text.secondary">
            End billing, withdraw every administrator and delete this tenant&rsquo;s content. It cannot be undone.
          </Typography>
        </Box>
        <Button color="error" variant="outlined" onClick={() => setOpen(true)}>
          Offboard…
        </Button>
      </Stack>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Offboard {tenant.name}?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              Billing ends today, every administrator loses access, and all of <strong>{tenant.id}</strong>&rsquo;s content,
              uploads and releases are deleted. The tenant&rsquo;s record and billing history are kept.
            </DialogContentText>
            <Alert severity="warning">
              The site keeps serving from its own hosting until you terminate it, but its contact forms stop delivering
              enquiries and it can no longer be rebuilt.
            </Alert>
            {Boolean(offboarding.error) && <Alert severity="error">{problemMessage(offboarding.error, 'The tenant was not offboarded.')}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={offboarding.isLoading}
            onClick={() =>
              offboard({ tenant: tenant.id })
                .unwrap()
                .then(() => setOpen(false))
                .catch(shown)
            }
          >
            Offboard {tenant.name}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

/**
 * The way back from a finished offboarding: the same record, active
 * again under its id, with nothing in it.
 *
 * Offered only when the platform says the tenant can be reinstated, and
 * pressable only from the moment it names — until then a deletion
 * attempt may still be running, and the platform refuses. The dialog says
 * what does not come back, because every one of those is a step the
 * operator takes next.
 */
function Reinstate({ tenant }: { tenant: OperatedTenant }) {
  const [open, setOpen] = useState(false)
  const [now, setNow] = useState(() => new Date())
  const [reinstate, reinstating] = useReinstateTenantMutation()
  const state = reinstatement(tenant, now)

  // Re-read the clock when the wait is over, so the button enables itself.
  const wait = state.state === 'waiting' ? state.in : undefined
  useEffect(() => {
    if (wait === undefined) return
    const timer = window.setTimeout(() => setNow(new Date()), Math.min(wait + 50, 2 ** 31 - 1))
    return () => window.clearTimeout(timer)
  }, [wait])

  if (state.state === 'unavailable') return null
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }}>
        <Box>
          <Typography variant="h3">Reinstate</Typography>
          <Typography variant="body2" color="text.secondary">
            Make this tenant active again under the same id. It comes back with its record and billing history, and
            nothing else.
          </Typography>
          {state.state === 'waiting' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Available from {state.from.toLocaleString()}, once no attempt to delete its content can still be running.
            </Typography>
          )}
        </Box>
        <Button variant="outlined" disabled={state.state !== 'ready'} onClick={() => setOpen(true)}>
          Reinstate…
        </Button>
      </Stack>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reinstate {tenant.name}?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              <strong>{tenant.id}</strong> becomes active again, keeping its record, billing history and log. Its content
              stays deleted: it has no content, administrators, domains or agreement until you add them.
            </DialogContentText>
            <Alert severity="info">Nothing is charged until you add an agreement on its Billing tab.</Alert>
            {Boolean(reinstating.error) && (
              <Alert severity="error">{problemMessage(reinstating.error, 'The tenant was not reinstated.')}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={reinstating.isLoading}
            onClick={() =>
              reinstate({ tenant: tenant.id })
                .unwrap()
                .then(() => setOpen(false))
                .catch(shown)
            }
          >
            Reinstate {tenant.name}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}
